import { useState, type ComponentPropsWithRef } from 'react';
import { Combobox } from '@base-ui/react/combobox';
import { IconAlertTriangle, IconChevronDown, IconRefresh, IconSearch } from '@tabler/icons-react';
import { motion, useReducedMotion } from 'motion/react';

import { MIN_SEARCH_LENGTH } from '../../../data/mockApi';
import type { RemoteOption, RemoteSearch } from '../../../data/types';
import { cn } from '../../../lib/cn';
import { DUR, EASE, PRESS } from '../../../interaction';
import { useRemoteSearch } from '../../../lib/useRemoteSearch';
import { useTranslation } from '../../../i18n/LocaleProvider';
import { Button } from '../../core/Button';
import { Spinner } from '../../core/Spinner';
import { ChoiceField } from './ChoiceField';
import { useFieldIds } from './useFieldIds';

/* =============================================================================
   Select — the async combobox
   -----------------------------------------------------------------------------
   The search runs on the API side and ONLY there. `filter={null}` is the whole
   of it: Base UI compiles a null filter to `() => true`
   (`@base-ui/react/combobox/root/AriaCombobox.js:230-233`), so it can never
   remove an option the API returned. Every keystroke is forwarded verbatim to
   `useRemoteSearch`, which owns the debounce, the minimum-length gate, the
   AbortController and the stale-response sequence guard — none of that is
   reimplemented here.

   The six keyboard behaviours were verified against the installed package
   rather than assumed; the two Base UI does NOT provide for a *typeable*
   combobox (Home/End highlight movement, open-on-focus) are implemented on top
   and marked below.
   ============================================================================= */

/** The shared motion vocabulary — `--ease-out` and `--dur-base`, read from the
 *  tokens rather than mirrored by hand. */
const POPUP_TRANSITION = () => ({ duration: DUR.base, ease: EASE.out });

/**
 * 44px minimum row height (`--target-min`), so no `dhd-target` overlay is
 * needed on the options. Highlight styling matches `Menu`'s item treatment.
 */
const ITEM_CLASS = cn(
  'flex min-h-[var(--target-min)] cursor-pointer flex-col justify-center gap-0.5 rounded-xs px-3 py-1.5',
  'font-body text-sm text-body outline-none',
  'data-[highlighted]:bg-primary-subtle data-[highlighted]:text-heading',
  'data-[selected]:font-semibold',
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-45',
  PRESS,
);

/** Shared shell for the four non-result popup states, so only one ever shows. */
const STATE_CLASS = 'flex items-start gap-2 px-3 py-2.5 text-sm text-muted';

type PopupState = 'error' | 'loading' | 'below-min' | 'empty' | 'results';

export interface SelectProps extends Omit<ComponentPropsWithRef<'div'>, 'id' | 'children'> {
  /** The API-side search. Receives the term and a signal it must honour. */
  search: RemoteSearch;
  /** The selected option. Controlled. */
  value?: RemoteOption | null;
  onValueChange?: (option: RemoteOption | null) => void;
  /** Characters required before a request is sent. Defaults to `MIN_SEARCH_LENGTH`. */
  minSearchLength?: number;
  /** Debounce in milliseconds. Defaults to 300 (see `useRemoteSearch`). */
  debounceMs?: number;
  /** Runs a search for the empty term too, so the field opens with suggestions. */
  initialLoad?: boolean;
  /** The open state, when the caller owns it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  label: string;
  placeholder?: string;
  error?: string | null;
  success?: string | null;
  hint?: string | null;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  /** Shown alongside a result set the API truncated. */
  truncatedHint?: string;
}

export function Select({
  search,
  value = null,
  onValueChange,
  minSearchLength = MIN_SEARCH_LENGTH,
  debounceMs,
  initialLoad,
  open: openProp,
  onOpenChange,
  label,
  placeholder,
  error,
  success,
  hint,
  required = false,
  disabled = false,
  id: providedId,
  truncatedHint,
  className,
  ...rest
}: SelectProps) {
  const { t } = useTranslation();
  const { id, describedBy, invalid } = useFieldIds(providedId, { error, success, hint });

  const {
    options,
    truncated,
    loading,
    error: searchError,
    query,
    setQuery,
    isBelowMinLength,
    retry,
  } = useRemoteSearch({ search, minSearchLength, debounceMs, initialLoad });

  const reduceMotion = useReducedMotion();

  // Open is controlled so that focus can open the popup and so a caller can
  // drive it. Base UI would otherwise manage `open` internally and there would
  // be no way in.
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpenControlled = openProp !== undefined;
  const isOpen = isOpenControlled ? openProp : uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (!isOpenControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  // An empty term is below the threshold too, unless `initialLoad` searches it.
  // `useRemoteSearch` bails out below `minSearchLength` on every non-empty term,
  // which its own `isBelowMinLength` already reports.
  const showBelowMinimum =
    isBelowMinLength || (query.length === 0 && minSearchLength > 0 && initialLoad !== true);
  const remaining = minSearchLength - query.length;

  // Exactly one state at a time, most-specific first.
  const popupState: PopupState = searchError
    ? 'error'
    : loading
      ? 'loading'
      : showBelowMinimum
        ? 'below-min'
        : options.length === 0
          ? 'empty'
          : 'results';

  /**
   * Home/End highlight movement — NOT provided by Base UI for a typeable
   * combobox, verified at two independent places:
   *   1. `floating-ui-react/hooks/useListNavigation.mjs:335` guards the
   *      Home/End branch behind `!typeableComboboxReference`, and the input IS
   *      the typeable reference (`element.mjs:43`).
   *   2. `combobox/input/ComboboxInput.js:302-315` consumes Home/End to move the
   *      text caret and returns before list navigation is consulted.
   *
   * `Combobox.Input` merges the caller's props LAST
   * (`ComboboxInput.js:161-231`, `}, validationProps]`), so this handler runs
   * before either of those and `preventBaseUIHandler()` keeps the caret still.
   *
   * ponytail: a synthetic `mousemove` on the target option is the only public
   * hook Base UI exposes for setting the highlight from outside —
   * `useListNavigation.mjs:433` `onMouseMove` -> `syncCurrentTarget`. If Base UI
   * ever ships an imperative highlight action on `actionsRef`, use it instead.
   * `movementX` is non-zero because WebKit drops zero-delta moves
   * (`useListNavigation.mjs:24`).
   */
  const moveHighlightToEdge = (
    input: HTMLInputElement,
    event: { preventDefault: () => void; preventBaseUIHandler: () => void },
    edge: 'first' | 'last',
  ) => {
    const listId = input.getAttribute('aria-controls');
    const list = listId ? input.ownerDocument.getElementById(listId) : null;
    const items = list ? list.querySelectorAll('[role="option"]') : [];
    if (items.length === 0) return;

    const next = items[edge === 'first' ? 0 : items.length - 1];
    if (!next) return;

    event.preventDefault();
    event.preventBaseUIHandler();
    (next as HTMLElement).scrollIntoView({ block: 'nearest' });

    const init: MouseEventInit & { movementX: number; movementY: number } = {
      bubbles: true,
      movementX: 1,
      movementY: 1,
    };
    next.dispatchEvent(new MouseEvent('mousemove', init));
  };

  return (
    <ChoiceField
      {...rest}
      id={id}
      label={label}
      required={required}
      disabled={disabled}
      error={error}
      success={success}
      hint={hint}
      className={className}
      /* Open on focus, which Base UI does not do: its `useClick` is
         `event: 'mousedown-only'` (`root/AriaCombobox.js:1015-1022`) and its
         only focus handler acts when the popup is *already* open
         (`useListNavigation.mjs:576-581`). Focus events bubble as `focusin`, so
         the wrapper sees the input's. */
      onFocus={(event) => {
        rest.onFocus?.(event);
        if (!disabled && !isOpen) setOpen(true);
      }}
    >
      <Combobox.Root<RemoteOption>
        items={options}
        /* The one line that keeps filtering on the API side. */
        filter={null}
        value={value}
        onValueChange={(next) => onValueChange?.(next)}
        /* Options are rebuilt on every response, so identity never matches. */
        isItemEqualToValue={(a, b) => a.value === b.value}
        inputValue={query}
        onInputValueChange={(next) => setQuery(next)}
        open={isOpen}
        onOpenChange={(next) => setOpen(next)}
        disabled={disabled}
        required={required}
      >
        <div className="relative">
          <span
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
            aria-hidden="true"
          >
            <IconSearch size={16} />
          </span>

          <Combobox.Input
            id={id}
            placeholder={placeholder}
            disabled={disabled}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            aria-required={required || undefined}
            className={cn(
              // The forms family geometry: --control-h-md (40px) with the 1px
              // token border, the same box a text input draws. It used to be
              // 48px tall on a 2px border, so a Select and an Input stacked in
              // one form did not line up.
              'h-[var(--control-h-md)] w-full appearance-none rounded-xs border-[length:var(--border-w)] bg-surface pl-9 pr-10 text-sm text-heading',
              'placeholder:text-muted',
              'transition-[border-color,background-color] duration-[var(--dur-fast)] ease-out-soft',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
              'disabled:cursor-not-allowed disabled:opacity-45',
              'data-[popup-open]:border-primary',
              invalid ? 'border-danger' : success ? 'border-success' : 'border-line-strong',
            )}
            onKeyDown={(event) => {
              if (event.key !== 'Home' && event.key !== 'End') return;
              if (!isOpen) return; // closed: Home/End keep their caret behaviour
              moveHighlightToEdge(
                event.currentTarget,
                event,
                event.key === 'Home' ? 'first' : 'last',
              );
            }}
          />

          <span
            className={cn(
              'pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted',
              'transition-transform duration-[var(--dur-fast)] ease-out-soft',
              isOpen && 'rotate-180',
            )}
            aria-hidden="true"
          >
            <IconChevronDown size={16} />
          </span>
        </div>

        {/* Portaled, so no ancestor's overflow can clip it. */}
        <Combobox.Portal>
          <Combobox.Positioner
            sideOffset={6}
            className="z-[var(--z-popover)] w-[var(--anchor-width)]"
          >
            <Combobox.Popup className="rounded-xs border-2 border-line-strong bg-surface-elevated shadow-hard outline-none">
              <motion.div
                initial={reduceMotion ? false : { opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={reduceMotion ? { duration: 0 } : POPUP_TRANSITION()}
              >
                {/* The listbox is mounted in EVERY popup state, not only the
                    results one. Base UI points `aria-controls` at it only while
                    it is registered — `combobox/root/AriaCombobox.js:1002` reads
                    `expanded ? listElement?.id : undefined`, and `listElement`
                    is set by `Combobox.List` (`combobox/list/ComboboxList.js:46`)
                    — so a popup that is open in the below-minimum, empty,
                    loading or error state would be a combobox expanded into
                    nothing, which is the axe `aria-required-attr` failure. An
                    empty listbox is a real state: it simply holds no options,
                    and carries no padding so the state panels below it do not
                    move. */}
                <Combobox.List
                  className={cn(
                    'overscroll-contain',
                    popupState === 'results' &&
                      'max-h-[min(18rem,var(--available-height))] overflow-y-auto p-1',
                  )}
                >
                  {(option: RemoteOption) => (
                    <Combobox.Item key={option.value} value={option} className={ITEM_CLASS}>
                      <span className="truncate">{option.label}</span>
                      {option.description ? (
                        <span className="truncate text-xs text-muted">
                          {option.description}
                        </span>
                      ) : null}
                    </Combobox.Item>
                  )}
                </Combobox.List>

                {popupState === 'results' && truncated ? (
                  <p className="border-t border-line-subtle px-3 py-2 text-xs text-muted">
                    {truncatedHint ?? t('ds.select.truncatedHint')}
                  </p>
                ) : null}

                {/* Base UI's Empty is a `role="status"` live region that shows its
                    children only while the list is empty, and its source asks for
                    the children to be conditional rather than the element. */}
                <Combobox.Empty className={STATE_CLASS}>
                  {popupState === 'empty'
                    ? query
                      ? t('ds.select.noMatchesFor', { query })
                      : t('ds.select.noMatches')
                    : null}
                </Combobox.Empty>

                {popupState === 'loading' ? (
                  <div className={STATE_CLASS} role="status" aria-busy="true">
                    <Spinner size="sm" />
                    <span>{t('ds.select.searching')}</span>
                  </div>
                ) : null}

                {popupState === 'below-min' ? (
                  <p className={STATE_CLASS} role="status">
                    {t('ds.select.minLength', { count: remaining })}
                  </p>
                ) : null}

                {popupState === 'error' ? (
                  <div className={cn(STATE_CLASS, 'flex-col items-stretch')} role="alert">
                    <p className="flex items-start gap-1.5 text-danger">
                      <span className="shrink-0" aria-hidden="true">
                        <IconAlertTriangle size={16} />
                      </span>
                      {searchError}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      leadingIcon={<IconRefresh size={14} />}
                      onClick={retry}
                    >
                      Retry
                    </Button>
                  </div>
                ) : null}
              </motion.div>
            </Combobox.Popup>
          </Combobox.Positioner>
        </Combobox.Portal>
      </Combobox.Root>
    </ChoiceField>
  );
}
